import { createElement, type ComponentProps } from 'react';

import { chakra, EloCardPrimary } from '@plurall/elo-v3';
import { Link } from '@tanstack/react-router';

import { clsx } from '@/lib/formatters/classNames';

import {
  dashboardLinkArrowRecipe,
  dashboardLinkCardRecipe,
  dashboardLinkIconRecipe,
  dashboardLinkLabelRecipe,
  dashboardLinkRootRecipe,
} from './dashboardLink.recipe';

const BaseDashboardLinkCardFactory = chakra(EloCardPrimary.Root, dashboardLinkCardRecipe);
const BaseDashboardLinkAnchorFactory = chakra('a', dashboardLinkRootRecipe);
const BaseDashboardLinkRouterLinkFactory = chakra(Link, dashboardLinkRootRecipe);
const BaseDashboardLinkLabelFactory = chakra('span', dashboardLinkLabelRecipe);
const BaseDashboardLinkIconFactory = chakra('span', dashboardLinkIconRecipe);
const BaseDashboardLinkArrowFactory = chakra('span', dashboardLinkArrowRecipe);

type BaseDashboardLinkCardFactoryProps = ComponentProps<typeof BaseDashboardLinkCardFactory>;
type BaseDashboardLinkAnchorFactoryProps = ComponentProps<typeof BaseDashboardLinkAnchorFactory>;
type BaseDashboardLinkRouterLinkFactoryProps = ComponentProps<typeof BaseDashboardLinkRouterLinkFactory>;

export type DashboardLinkCardFactoryProps = Omit<
  BaseDashboardLinkCardFactoryProps,
  'behavior' | 'cardRadius' | 'status' | 'variant' | 'padding' | 'minHeight'
>;
export type DashboardLinkAnchorFactoryProps = BaseDashboardLinkAnchorFactoryProps;
export type DashboardLinkRouterLinkFactoryProps = BaseDashboardLinkRouterLinkFactoryProps;
export type DashboardLinkLabelFactoryProps = ComponentProps<typeof BaseDashboardLinkLabelFactory>;
export type DashboardLinkIconFactoryProps = ComponentProps<typeof BaseDashboardLinkIconFactory>;
export type DashboardLinkArrowFactoryProps = ComponentProps<typeof BaseDashboardLinkArrowFactory>;

export function DashboardLinkCardFactory(props: DashboardLinkCardFactoryProps): ReturnType<typeof createElement> {
  return createElement(BaseDashboardLinkCardFactory, {
    ...props,
    behavior: 'static',
    cardRadius: 'medium',
    'data-test-id': 'elo-card',
    minHeight: '56px',
    padding: 'padding.large',
    status: 'neutral',
    variant: 'solid',
  });
}

export function DashboardLinkAnchorFactory({
  className,
  ...props
}: DashboardLinkAnchorFactoryProps): ReturnType<typeof createElement> {
  return createElement(BaseDashboardLinkAnchorFactory, {
    ...props,
    className: clsx(className, 'dashboard-link', 'item'),
  });
}

export function DashboardLinkRouterLinkFactory({
  className,
  ...props
}: DashboardLinkRouterLinkFactoryProps): ReturnType<typeof createElement> {
  return createElement(BaseDashboardLinkRouterLinkFactory, {
    ...props,
    className: clsx(className, 'dashboard-link', 'item'),
  });
}

export function DashboardLinkLabelFactory(props: DashboardLinkLabelFactoryProps): ReturnType<typeof createElement> {
  return createElement(BaseDashboardLinkLabelFactory, props);
}

export function DashboardLinkIconFactory(props: DashboardLinkIconFactoryProps): ReturnType<typeof createElement> {
  return createElement(BaseDashboardLinkIconFactory, props);
}

export function DashboardLinkArrowFactory(props: DashboardLinkArrowFactoryProps): ReturnType<typeof createElement> {
  return createElement(BaseDashboardLinkArrowFactory, props);
}
